import React, { useState, useEffect } from 'react';
import { useConfirm } from "../../hooks/useConfirm";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Users, MessageSquare, CreditCard, Activity, Phone, FileText } from 'lucide-react';
import Layout from '../../components/Layout';
import IAConfigPanel from '../../components/IAConfigPanel';
import IntegracoesCliente from '../../components/IntegracoesCliente';
import { clientesAPI, usuariosAPI, instancesAPI, faturamentoAPI } from '../../services/api';
import { planosAPI } from '../../services/planosService';
import { useTheme } from '../../contexts/ThemeContext';
import { useNivelAcesso } from '../../hooks/useNivelAcesso';
import { PageSkeleton, SkeletonCard } from '../../components/Skeleton';

const ClientDetails = () => {
  const showConfirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useNivelAcesso();
  const { darkMode } = useTheme();
  const [cliente, setCliente] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [instancia, setInstancia] = useState(null);
  const [uso, setUso] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emitindo, setEmitindo] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clienteRes, usuariosRes, planosRes] = await Promise.all([
        clientesAPI.get(id),
        usuariosAPI.list(),
        planosAPI.list()
      ]);

      setCliente(clienteRes.data);
      const usuariosFiltrados = (usuariosRes.data.usuarios || []).filter(u => {
        const clienteId = typeof u.cliente_id === 'object' ? u.cliente_id?.id : u.cliente_id;
        return clienteId === id;
      });
      setUsuarios(usuariosFiltrados);
      setPlanos(planosRes.data || []);

      try {
        const [usoRes, instanciaRes] = await Promise.all([
          planosAPI.getUso(id),
          instancesAPI.getStatus(id),
        ]);
        setUso(usoRes.data);
        setInstancia(instanciaRes.data.data || instanciaRes.data);
      } catch (error) {
        console.error('Erro ao carregar uso/instância:', error);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarPlano = async (planoId) => {
    showConfirm({
      title: 'Alterar Plano',
      message: 'Deseja alterar o plano deste cliente?',
      confirmText: 'Alterar',
      type: 'warning',
      onConfirm: async () => {
        try {
          await planosAPI.alterarPlano(id, planoId);
          toast.success('Plano alterado com sucesso');
          loadData();
        } catch (error) {
          toast.error('Erro ao alterar plano');
        }
      }
    });
  };

  const handleEmitirMensalidade = async () => {
    showConfirm({
      title: 'Adiantar mensalidade do próximo ciclo',
      message: `Isto vai gerar um boleto/Pix REAL no Banco Inter para "${cliente?.nome}", referente ao PRÓXIMO ciclo. O ciclo atual continua fechando na data normal, e o sistema não vai cobrar de novo quando ele virar. Confirmar emissão?`,
      confirmText: 'Emitir boleto',
      type: 'warning',
      onConfirm: async () => {
        setEmitindo(true);
        try {
          const res = await faturamentoAPI.emitirManual(id);
          const d = res.data || {};
          toast.success(`Mensalidade emitida: R$ ${Number(d.valor || 0).toFixed(2)} — venc. ${d.vencimento || '—'}`);
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Erro ao emitir mensalidade');
        } finally {
          setEmitindo(false);
        }
      }
    });
  };

  if (loading) {
    return (
      <Layout>
        <PageSkeleton>
          <SkeletonCard linhas={6} />
        </PageSkeleton>
      </Layout>
    );
  }

  if (!cliente) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Cliente não encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/clients')}
              className={`p-2 rounded-md ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={darkMode ? 'text-white' : 'text-gray-900'} />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {cliente.nome}
              </h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Detalhes do cliente
              </p>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Usuários</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {usuarios?.length || 0}
                </p>
              </div>
              <Users className={`h-8 w-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
          </div>

          <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Mensagens</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {uso?.uso.mensagens_enviadas || 0}
                </p>
              </div>
              <MessageSquare className={`h-8 w-8 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            </div>
          </div>

          <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Plano</p>
                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {uso?.plano_atual?.nome || 'Sem plano'}
                </p>
              </div>
              <CreditCard className={`h-8 w-8 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
          </div>

          <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>WhatsApp</p>
                <p className={`text-sm font-bold ${
                  instancia?.status === 'conectada' 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {instancia?.status || 'Desconectado'}
                </p>
              </div>
              <Phone className={`h-8 w-8 ${
                instancia?.status === 'conectada'
                  ? darkMode ? 'text-green-400' : 'text-green-600'
                  : darkMode ? 'text-red-400' : 'text-red-600'
              }`} />
            </div>
          </div>
        </div>

        {/* Uso do Plano */}
        {uso && (
          <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Uso do Plano
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Mensagens
                  </span>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {uso.uso.mensagens_enviadas} / {uso.uso.limite_mensagens === -1 ? '∞' : uso.uso.limite_mensagens}
                  </span>
                </div>
                <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div 
                    className={`h-2 rounded-full ${
                      uso.uso.percentual_usado >= 90 ? 'bg-red-500' :
                      uso.uso.percentual_usado >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(uso.uso.percentual_usado, 100)}%` }}
                  />
                </div>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {uso.uso.dias_restantes} dias até o reset
                </p>
              </div>
            </div>

            {/* Emissão manual de mensalidade (admin, plano pago) */}
            {isAdmin && parseFloat(uso?.plano_atual?.preco || 0) > 0 && (
              <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={handleEmitirMensalidade}
                  disabled={emitindo}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-nomoz-orange text-white hover:opacity-90 disabled:opacity-50"
                >
                  <FileText className="h-4 w-4" />
                  {emitindo ? 'Emitindo...' : 'Adiantar mensalidade (próximo ciclo)'}
                </button>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Gera antecipadamente o boleto/Pix do próximo ciclo. O ciclo atual fecha normalmente e o sistema não cobra em duplicidade.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Instância WhatsApp */}
        {instancia && (
          <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Instância WhatsApp
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</p>
                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {instancia.status || instancia.data?.status}
                </p>
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Número</p>
                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {instancia.numero_whatsapp || instancia.data?.numero_whatsapp || 'N/A'}
                </p>
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>IA Ativa</p>
                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {(instancia.ia_config_id || instancia.data?.ia_config_id) ? 'Sim' : 'Não'}
                </p>
                {isAdmin() && (
                  <span className="text-xs text-nomoz-orange">
                    Configuração abaixo
                  </span>
                )}
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Regras Ativas</p>
                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {(instancia.regras_automaticas_ativas || instancia.data?.regras_automaticas_ativas) ? 'Sim' : 'Não'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Configuração de IA */}
        {isAdmin() && (
          <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Configuração de IA
            </h2>
            <IAConfigPanel clienteId={id} flat />
          </div>
        )}

        {/* Usuários */}
        <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Usuários ({usuarios?.length || 0})
          </h2>
          <div className="space-y-3">
            {usuarios && usuarios.length > 0 ? usuarios.map(usuario => (
              <div key={usuario.id} className={`flex items-center justify-between p-3 rounded ${
                darkMode ? 'bg-zinc-700' : 'bg-gray-50'
              }`}>
                <div>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {usuario.nome}
                  </p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {usuario.email}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${
                  usuario.status
                    ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                    : darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                }`}>
                  {usuario.status ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            )) : (
              <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Nenhum usuário encontrado
              </p>
            )}
          </div>
        </div>

        {/* Alterar Plano */}
        <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Alterar Plano
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {planos.map(plano => {
              const isAtual = uso?.plano_atual?.id === plano.id;
              return (
                <div
                  key={plano.id}
                  className={`p-4 rounded-lg border-2 ${
                    isAtual
                      ? 'border-blue-500'
                      : darkMode ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  <h3 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {plano.nome}
                  </h3>
                  <p className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    R$ {parseFloat(plano.preco).toFixed(2)}
                  </p>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {plano.limite_mensagens_mes === -1 ? '∞' : plano.limite_mensagens_mes} msgs/mês
                  </p>
                  {!isAtual && (
                    <button
                      onClick={() => handleAlterarPlano(plano.id)}
                      className="w-full gradient-button py-2 text-sm text-white rounded"
                    >
                      Selecionar
                    </button>
                  )}
                  {isAtual && (
                    <div className="w-full py-2 text-sm text-center bg-blue-500 text-white rounded">
                      Plano Atual
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Configurações de Campanhas */}
        <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            ⚙️ Configurações de Campanhas
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rodapé Opt-out Obrigatório</p>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Adiciona rodapé de descadastramento em todos os templates (obrigatório para clientes políticos)
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const novoValor = !cliente.footer_optout_obrigatorio;
                    await clientesAPI.update({ id, footer_optout_obrigatorio: novoValor });
                    setCliente({ ...cliente, footer_optout_obrigatorio: novoValor });
                    toast.success(novoValor ? 'Rodapé opt-out ativado' : 'Rodapé opt-out desativado');
                  } catch (e) { toast.error('Erro ao atualizar'); }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  cliente.footer_optout_obrigatorio ? 'bg-nomoz-orange' : darkMode ? 'bg-zinc-600' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  cliente.footer_optout_obrigatorio ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {cliente.footer_optout_obrigatorio && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Texto do Rodapé
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={cliente.footer_optout_texto || ''}
                    onChange={(e) => setCliente({ ...cliente, footer_optout_texto: e.target.value })}
                    placeholder="Responda SAIR para não receber mais mensagens."
                    className={`flex-1 rounded-lg px-4 py-2 ${
                      darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'
                    } border`}
                  />
                  <button
                    onClick={async () => {
                      try {
                        await clientesAPI.update({ id, footer_optout_texto: cliente.footer_optout_texto });
                        toast.success('Texto do rodapé salvo');
                      } catch (e) { toast.error('Erro ao salvar'); }
                    }}
                    className="px-4 py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg text-sm"
                  >
                    Salvar
                  </button>
                </div>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Quando o contato responder "SAIR" após receber uma campanha nas últimas 24h, será removido automaticamente da lista.
                </p>
              </div>
            )}
          </div>
        </div>


        {/* Configurações Avançadas de Campanha */}
        <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            ⚡ Pool de Disparo
          </h2>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Habilitar Pool de Disparo</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Fila persistida em banco e envio por processo separado: reiniciar a API não
                interrompe mais a campanha. Requer POOL_ENABLED ligado no servidor.
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  const novoValor = !cliente.pool_disparo_habilitado;
                  await clientesAPI.update({ id, pool_disparo_habilitado: novoValor });
                  setCliente({ ...cliente, pool_disparo_habilitado: novoValor });
                  toast.success(novoValor ? 'Pool de disparo ativado' : 'Pool de disparo desativado');
                } catch (e) { toast.error('Erro ao atualizar'); }
              }}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                cliente.pool_disparo_habilitado ? 'bg-nomoz-orange' : darkMode ? 'bg-zinc-600' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                cliente.pool_disparo_habilitado ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          {cliente.pool_disparo_habilitado && (
            <p className={`mt-4 pt-4 border-t text-sm ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
              Campanhas novas deste cliente passam a usar a fila. Campanhas já em andamento
              continuam no caminho anterior até terminarem.
            </p>
          )}
        </div>

        <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            🚀 Configurações Avançadas de Campanha
          </h2>
          
          {/* Toggle habilitar */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Habilitar Configurações Avançadas</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Controle de velocidade, janela de horário e limites de envio por campanha
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  const novoValor = !cliente.campanha_avancada_habilitada;
                  await clientesAPI.update({ id, campanha_avancada_habilitada: novoValor });
                  setCliente({ ...cliente, campanha_avancada_habilitada: novoValor });
                  toast.success(novoValor ? 'Configurações avançadas ativadas' : 'Configurações avançadas desativadas');
                } catch (e) { toast.error('Erro ao atualizar'); }
              }}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                cliente.campanha_avancada_habilitada ? 'bg-nomoz-orange' : darkMode ? 'bg-zinc-600' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                cliente.campanha_avancada_habilitada ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          {cliente.campanha_avancada_habilitada && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              {/* Perfil de Disparo Padrão */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Perfil de Disparo Padrão
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { id: 'gradual', label: '🐢 Gradual', desc: '60-120s, 20/h', delay_min: 60, delay_max: 120, lote: 20 },
                    { id: 'moderado', label: '⚡ Moderado', desc: '15-30s, 60/h', delay_min: 15, delay_max: 30, lote: 60 },
                    { id: 'agressivo', label: '🔥 Agressivo', desc: '5-10s, 150/h', delay_min: 5, delay_max: 10, lote: 150 },
                    { id: 'personalizado', label: '🎯 Personalizado', desc: 'Valores custom' },
                  ].map(perfil => (
                    <button
                      key={perfil.id}
                      onClick={async () => {
                        const update = { perfil_disparo_padrao: perfil.id };
                        if (perfil.delay_min) {
                          update.campanha_delay_min = perfil.delay_min;
                          update.campanha_delay_max = perfil.delay_max;
                          update.campanha_lote_max_hora = perfil.lote;
                        }
                        try {
                          await clientesAPI.update({ id, ...update });
                          setCliente({ ...cliente, ...update });
                          toast.success(`Perfil ${perfil.label} definido como padrão`);
                        } catch (e) { toast.error('Erro ao atualizar'); }
                      }}
                      className={`p-3 rounded-lg border text-left transition ${
                        cliente.perfil_disparo_padrao === perfil.id
                          ? 'border-nomoz-orange bg-nomoz-orange/10'
                          : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{perfil.label}</p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {cliente.perfil_disparo_padrao === perfil.id && perfil.id !== 'personalizado'
                          ? `${cliente.campanha_delay_min ?? perfil.delay_min}-${cliente.campanha_delay_max ?? perfil.delay_max}s, ${cliente.campanha_lote_max_hora ?? perfil.lote}/h`
                          : perfil.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Janela de Horário */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Horário Início
                  </label>
                  <input
                    type="time"
                    value={cliente.campanha_horario_inicio || '08:00'}
                    onChange={(e) => setCliente({ ...cliente, campanha_horario_inicio: e.target.value })}
                    className={`w-full rounded-lg px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Horário Fim
                  </label>
                  <input
                    type="time"
                    value={cliente.campanha_horario_fim || '19:00'}
                    onChange={(e) => setCliente({ ...cliente, campanha_horario_fim: e.target.value })}
                    className={`w-full rounded-lg px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>
              </div>

              {/* Delay e Lote */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Delay Mín (seg)
                  </label>
                  <input
                    type="number"
                    value={cliente.campanha_delay_min || 15}
                    onChange={(e) => setCliente({ ...cliente, campanha_delay_min: parseInt(e.target.value) })}
                    className={`w-full rounded-lg px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Delay Máx (seg)
                  </label>
                  <input
                    type="number"
                    value={cliente.campanha_delay_max || 30}
                    onChange={(e) => setCliente({ ...cliente, campanha_delay_max: parseInt(e.target.value) })}
                    className={`w-full rounded-lg px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Máx por Hora
                  </label>
                  <input
                    type="number"
                    value={cliente.campanha_lote_max_hora || 60}
                    onChange={(e) => setCliente({ ...cliente, campanha_lote_max_hora: parseInt(e.target.value) })}
                    className={`w-full rounded-lg px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>
              </div>

              {/* Dias da Semana */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Dias da Semana
                </label>
                <div className="flex gap-2">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dia, i) => {
                    const dias = cliente.campanha_dias_semana || [1,2,3,4,5];
                    const ativo = dias.includes(i);
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          const novosDias = ativo ? dias.filter(d => d !== i) : [...dias, i].sort();
                          setCliente({ ...cliente, campanha_dias_semana: novosDias });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                          ativo
                            ? 'bg-nomoz-orange text-white'
                            : darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {dia}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Perfis de Disparo Liberados */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🔓 Perfis Liberados para este Cliente
                </label>
                <p className={`text-xs mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Controle quais velocidades de disparo este cliente pode usar. Clientes novos devem começar apenas com Gradual.
                </p>
                <div className="flex gap-3">
                  {[
                    { id: 'gradual', label: '🐢 Gradual' },
                    { id: 'moderado', label: '⚡ Moderado' },
                    { id: 'agressivo', label: '🔥 Agressivo' },
                    { id: 'personalizado', label: '🎯 Custom' },
                  ].map(perfil => {
                    const liberados = cliente.perfis_disparo_liberados || ['gradual'];
                    const ativo = liberados.includes(perfil.id);
                    return (
                      <button
                        key={perfil.id}
                        onClick={() => {
                          const novos = ativo
                            ? liberados.filter(p => p !== perfil.id)
                            : [...liberados, perfil.id];
                          setCliente({ ...cliente, perfis_disparo_liberados: novos });
                        }}
                        className={`flex-1 p-3 rounded-lg border-2 text-center transition ${
                          ativo
                            ? 'border-green-500 bg-green-500/10'
                            : darkMode ? 'border-gray-700 bg-zinc-800 opacity-50' : 'border-gray-200 bg-gray-50 opacity-50'
                        }`}
                      >
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{perfil.label}</p>
                        <p className={`text-xs mt-1 ${ativo ? 'text-green-500' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {ativo ? '✅ Liberado' : '🔒 Travado'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Blocklist de Grupos */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🚫 Blocklist de Grupos WhatsApp
                </label>
                <p className={`text-xs mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Números que serão ignorados na análise de sentimento e métricas de grupos (ex: seus próprios números de disparo).
                </p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Ex: 5519999999999"
                    id="blocklist-input"
                    className={`flex-1 rounded-lg px-3 py-2 border text-sm ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target;
                        const num = input.value.replace(/\D/g, '');
                        if (num.length >= 10) {
                          const lista = cliente.blocklist_grupos || [];
                          if (!lista.includes(num)) {
                            setCliente({ ...cliente, blocklist_grupos: [...lista, num] });
                          }
                          input.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('blocklist-input');
                      const num = input.value.replace(/\D/g, '');
                      if (num.length >= 10) {
                        const lista = cliente.blocklist_grupos || [];
                        if (!lista.includes(num)) {
                          setCliente({ ...cliente, blocklist_grupos: [...lista, num] });
                        }
                        input.value = '';
                      }
                    }}
                    className="px-3 py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg text-sm"
                  >
                    Adicionar
                  </button>
                </div>
                {(cliente.blocklist_grupos || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(cliente.blocklist_grupos || []).map((num, i) => (
                      <span key={i} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
                      }`}>
                        {num}
                        <button onClick={() => {
                          setCliente({ ...cliente, blocklist_grupos: cliente.blocklist_grupos.filter((_, idx) => idx !== i) });
                        }} className="ml-1 hover:text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Botão Salvar Config */}
              <button
                onClick={async () => {
                  try {
                    await clientesAPI.update({
                      id,
                      perfil_disparo_padrao: cliente.perfil_disparo_padrao,
                      campanha_horario_inicio: cliente.campanha_horario_inicio,
                      campanha_horario_fim: cliente.campanha_horario_fim,
                      campanha_delay_min: cliente.campanha_delay_min,
                      campanha_delay_max: cliente.campanha_delay_max,
                      campanha_lote_max_hora: cliente.campanha_lote_max_hora,
                      campanha_dias_semana: cliente.campanha_dias_semana,
                      perfis_disparo_liberados: cliente.perfis_disparo_liberados,
                      blocklist_grupos: cliente.blocklist_grupos,
                    });
                    toast.success('Configurações avançadas salvas');
                  } catch (e) { toast.error('Erro ao salvar'); }
                }}
                className="w-full py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg text-sm font-medium"
              >
                💾 Salvar Configurações Padrão
              </button>
            </div>
          )}
        </div>

        {/* Integrações */}
        <IntegracoesCliente clienteId={id} clienteNome={cliente?.nome} />
      </div>
    </Layout>
  );
};

export default ClientDetails;
